import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserPermissionsUI, UserRoleUI } from './models/auth-ui.model';
import { UserAuthorisationApi } from './models/auth-api.model';
import { AuthAdapter } from './adapters/auth.adapter';
import { AccountInfo } from '@azure/msal-common';
import { CustomersService } from 'src/app/ecommerce/shared/services/customers/customers.service';
import { CustomerLiteSharedUIModel } from '@socialonce/enganchesaragon-adminnav-lib';

export interface SessionDataUI {
    roles: UserRoleUI[];
    customer: CustomerLiteSharedUIModel;
}

@Injectable({
    providedIn: 'root',
})
export class ApiAuthService {
    authAdapter = new AuthAdapter();
    private userRoles = new BehaviorSubject<UserRoleUI[] | null>(null);
    perms: UserPermissionsUI | null = null;
    employeeId: string | null = null;
    userData$ = new ReplaySubject<AccountInfo | null>();
    customerData$ = new ReplaySubject<CustomerLiteSharedUIModel>(1);
    isLoadingCustomerRoles = false;
    hasErrorsOnLoadingCustomerRoles = false;

    private sessionLoading$: Observable<SessionDataUI> | null = null;

    constructor(
        private msalService: MsalService,
        private http: HttpClient,
        private customersService: CustomersService
    ) {}

    login(): void {
        this.cleanUserAuths();
        this.msalService.loginRedirect({ scopes: ['user.read', 'openid', 'profile'] });
    }

    redirectToResetPassword(): void {
        window.location.href = `${environment.assets.b2c.resetPasswordUrl}&client_id=7d679aaf-57bb-4aee-a166-f44fb59ceb23&nonce=defaultNonce&redirect_uri=https%3A%2F%2Fwww.enganchesaragon.com%2Fauth&scope=openid&response_type=id_token&prompt=login`;
    }

    logout(redirectToLogin = false): void {
        this.cleanUserAuths();

        if (redirectToLogin) this.msalService.logout({ postLogoutRedirectUri: '/auth-redirect' });
        else this.msalService.logout();
    }

    getRoles(): Observable<UserRoleUI[] | null> {
        this.userData$.next(this.msalService.instance.getActiveAccount());

        if (!this.isLoggedIn()) {
            this.cleanUserAuths();
            return of(null);
        }

        if (this.userRoles.getValue() !== null) {
            return of(this.userRoles.getValue());
        }

        return this.loadRolesAndCustomer().pipe(map(session => session.roles));
    }

    getCustomer(): Observable<CustomerLiteSharedUIModel> {
        return this.customersService.getFullCustomer();
    }

    /** Loads Permissions, then FullCustomer, in that order (single shared in-flight request). */
    loadRolesAndCustomer(): Observable<SessionDataUI> {
        this.userData$.next(this.msalService.instance.getActiveAccount());

        if (!this.isLoggedIn()) {
            this.cleanUserAuths();
            return throwError(() => new Error('User is not logged in'));
        }

        if (!this.sessionLoading$) {
            this.isLoadingCustomerRoles = true;
            this.sessionLoading$ = this.fetchPermissionsAndCustomer().pipe(
                shareReplay({ bufferSize: 1, refCount: false }),
                finalize(() => {
                    this.sessionLoading$ = null;
                    this.isLoadingCustomerRoles = false;
                })
            );
        }

        return this.sessionLoading$;
    }

    invalidateSessionCache(): void {
        this.userRoles.next(null);
        this.perms = null;
        this.employeeId = null;
        this.isLoadingCustomerRoles = false;
        this.hasErrorsOnLoadingCustomerRoles = false;
        this.sessionLoading$ = null;
    }

    reloadSession(): Observable<SessionDataUI> {
        this.invalidateSessionCache();
        return this.loadRolesAndCustomer();
    }

    reloadApplication(): void {
        this.invalidateSessionCache();
        window.location.reload();
    }

    getPerms(): UserPermissionsUI {
        return { ...this.perms };
    }

    getCustomerId(): string | null {
        return this.employeeId;
    }

    isLoggedIn(): boolean {
        return this.msalService.instance.getAllAccounts().length > 0;
    }

    resolveLoggedEmail(customer: CustomerLiteSharedUIModel): string {
        if (customer.loggedEmail) {
            return customer.loggedEmail;
        }
        return this.msalService.instance.getActiveAccount()?.username ?? '';
    }

    private fetchPermissionsAndCustomer(): Observable<SessionDataUI> {
        return this.loadUserRoles().pipe(
            switchMap(roles => {
                if (roles === null) {
                    return throwError(() => new Error('Failed to load user permissions'));
                }
                return this.getCustomer().pipe(
                    map(customer => ({ roles, customer })),
                    tap(({ customer }) => {
                        this.customerData$.next(customer);
                    })
                );
            }),
            catchError(err => {
                this.hasErrorsOnLoadingCustomerRoles = true;
                this.isLoadingCustomerRoles = false;
                this.sessionLoading$ = null;
                return throwError(err);
            }),
            tap(() => {
                this.hasErrorsOnLoadingCustomerRoles = false;
            })
        );
    }

    private loadUserRoles(): Observable<UserRoleUI[] | null> {
        return this.http
            .get<UserAuthorisationApi>(
                `${environment.endpoints.microservices.user}/Permissions`,
                {}
            )
            .pipe(
                map(authorisationObj => this.authAdapter.convertGetResponseToUI(authorisationObj)),
                switchMap(authorisationObj => {
                    this.userRoles.next(authorisationObj.roles);
                    this.perms = authorisationObj.permissions;
                    this.employeeId = authorisationObj.employeeId;
                    return of(authorisationObj.roles);
                }),
                catchError(() => {
                    this.hasErrorsOnLoadingCustomerRoles = true;
                    this.isLoadingCustomerRoles = false;
                    this.sessionLoading$ = null;
                    return of(null);
                })
            );
    }

    private cleanUserAuths() {
        this.userRoles.next(null);
        this.perms = null;
        this.employeeId = null;
        this.sessionLoading$ = null;
    }
}
